
import { useMediaQuery } from "@/hooks/use-media-query";
import { ReactNode, useState } from "react";
import { Button } from "@/components/ui/button";
import { Menu, Users, Book, LogOut, Settings, Crown, Home, UserCheck, Video, BarChart3 } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  SidebarProvider,
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarFooter,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  TooltipProvider,
} from "@/components/ui/sidebar";


interface AdminLayoutProps {
  children: ReactNode;
  activeTab: "users" | "books" | "specialists" | "configuration" | "sessions" | "metrics";
}

export function AdminLayout({ children, activeTab }: AdminLayoutProps) {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const location = useLocation();
  const { user } = useAuth();
  
  // Determine the appropriate dashboard based on user role
  const userRole = user?.user_metadata?.role;
  const dashboardRoute = userRole === 'specialist' ? '/coach-dashboard' : '/dashboard';
  const signInRoute = userRole === 'specialist' ? '/coach-signin' : '/admin-signin';
  
  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      toast({
        title: "Signed out",
        description: "You have been signed out successfully",
      });
      navigate(signInRoute);
    } catch (error) {
      console.error("Error signing out:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to sign out. Please try again.",
      });
    }
  };

  const navItems = [
    {
      name: "User Management",
      icon: Users,
      active: activeTab === "users",
      onClick: () => navigate("/admin")
    },
    {
      name: "Feature Metrics",
      icon: BarChart3,
      active: activeTab === "metrics",
      onClick: () => navigate("/admin?tab=metrics")
    },
    {
      name: "Books Library",
      icon: Book,
      active: activeTab === "books", 
      onClick: () => navigate("/admin?tab=books")
    },
    {
      name: "Specialists",
      icon: UserCheck,
      active: activeTab === "specialists",
      onClick: () => navigate("/admin?tab=specialists")
    },
    {
      name: "Live Sessions",
      icon: Video,
      active: activeTab === "sessions",
      onClick: () => navigate("/admin?tab=sessions")
    },
    {
      name: "Configuration",
      icon: Settings,
      active: location.pathname.startsWith("/configuration"),
      onClick: () => navigate("/configuration")
    }
  ];

  // Desktop layout using shadcn/ui Sidebar
  if (isDesktop) {
    return (
      <TooltipProvider>
        <SidebarProvider>
          <div className="flex min-h-screen w-full bg-background">
            {/* Desktop Sidebar */}
            <Sidebar className="border-r border-border/50 bg-card">
              <SidebarHeader className="p-6 border-b border-border/50 bg-gradient-to-b from-muted/30 to-transparent">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-gradient-to-br from-primary/20 via-primary/10 to-primary/5 border border-primary/20 shadow-sm">
                    <svg 
                      className="h-5 w-5 text-primary" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                      />
                    </svg>
                  </div>
                  <h1 className="text-xl font-bold tracking-tight text-foreground">Admin Panel</h1>
                </div>
              </SidebarHeader>
              
              <SidebarContent className="px-3 py-4 bg-gradient-to-b from-transparent to-muted/20">
                <SidebarMenu className="space-y-1">
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      onClick={() => navigate(dashboardRoute)}
                      tooltip="Back to Dashboard"
                      className="h-11 rounded-lg hover:bg-accent/80 hover:shadow-sm transition-all duration-200"
                    >
                      <Home className="h-4 w-4" />
                      <span className="font-medium">Back to Dashboard</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  {navItems.map((item) => (
                    <SidebarMenuItem key={item.name}>
                      <SidebarMenuButton
                        isActive={item.active}
                        onClick={item.onClick}
                        tooltip={item.name}
                        className="h-11 rounded-lg hover:bg-accent/80 hover:shadow-sm transition-all duration-200 data-[active=true]:bg-primary/10 data-[active=true]:text-primary data-[active=true]:border-l-4 data-[active=true]:border-primary data-[active=true]:shadow-sm"
                      >
                        <item.icon className="h-4 w-4" />
                        <span className="font-medium">{item.name}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarContent>
              
              <SidebarFooter className="p-4 border-t border-border/50 bg-gradient-to-t from-muted/30 to-transparent">
                <div className="space-y-2">
                  <Button
                    variant="outline"
                    className="w-full justify-start border-destructive/30 text-destructive hover:bg-destructive/10 hover:text-destructive hover:border-destructive/50 h-10 transition-all duration-200"
                    onClick={handleSignOut}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign Out
                  </Button>
                </div>
              </SidebarFooter>
            </Sidebar>
            
            {/* Main Content */}
            <main className="flex-1 overflow-auto bg-muted/30">
              <div className="p-8">
                {children}
              </div>
            </main>
        </div>
      </SidebarProvider>
      </TooltipProvider>
    );
  }

  // Mobile layout with bottom navigation and side drawer
  return (
    <div className="flex flex-col min-h-screen bg-background">
      {/* Mobile Header */}
      <div className="bg-background border-b border-border p-4 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-3">
          <div className="p-1.5 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 border border-primary/20">
            <svg 
              className="h-4 w-4 text-primary" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
              />
            </svg>
          </div>
          <h1 className="text-lg font-bold tracking-tight">Admin Panel</h1>
        </div>
        <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
          <Button variant="ghost" size="icon" className="h-10 w-10" onClick={() => setMobileNavOpen(true)}>
            <Menu className="h-5 w-5" />
          </Button>
          <SheetContent side="left" className="w-72">
            <SheetHeader className="border-b pb-4 mb-4">
              <SheetTitle className="text-xl font-bold">Admin Panel</SheetTitle>
            </SheetHeader>
            <nav className="flex flex-col gap-2 mt-4">
              <Button
                variant="ghost"
                className="justify-start h-11 rounded-lg"
                onClick={() => {
                  navigate(dashboardRoute);
                  setMobileNavOpen(false);
                }}
              >
                <Home className="mr-3 h-4 w-4" />
                Back to Dashboard
              </Button>
              {navItems.map((item) => (
                <Button
                  key={item.name}
                  variant={item.active ? "default" : "ghost"}
                  className="justify-start h-11 rounded-lg"
                  onClick={() => {
                    item.onClick();
                    setMobileNavOpen(false);
                  }}
                >
                  <item.icon className="mr-3 h-4 w-4" />
                  <span className="font-medium">{item.name}</span>
                </Button>
              ))}
              <div className="border-t my-4 pt-4 space-y-3">
                <Button
                  variant="outline" 
                  className="justify-start border-destructive/30 text-destructive hover:bg-destructive/10 hover:text-destructive h-10 w-full"
                  onClick={handleSignOut}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </Button>
              </div>
            </nav>
          </SheetContent>
        </Sheet>
      </div>
      
      {/* Main Content */}
      <div className="flex-1 overflow-auto p-6 bg-muted/20">
        {children}
      </div>
      
      {/* Mobile Bottom Navigation */}
      <div className="bg-background border-t border-border p-2 grid grid-cols-3 gap-1 md:hidden shadow-sm">
        {navItems.slice(0, 3).map((item) => (
          <Button
            key={item.name}
            variant={item.active ? "default" : "ghost"}
            className="flex flex-col items-center justify-center py-3 h-auto rounded-lg"
            onClick={item.onClick}
          >
            <item.icon className="h-5 w-5 mb-1" />
            <span className="text-xs font-medium">{item.name.split(' ')[0]}</span>
          </Button>
        ))}
      </div>
    </div>
  );
}
